import React from "react";
import Select, { components, Props as SelectProps } from "react-select";
import AsyncSelect from "react-select/async";
import { useFormikContext } from "formik";
import customStyles from "../ReactSelect/customStyles";
import { CustomDropdownIndicator } from "../ReactSelect/CustomDropdownIndicator";

interface Option {
    value: string | number;
    label: string;
}

const InputWithAutoComplete = (props: any) => {
    const autoComplete = props.selectProps.inputAutoComplete ?? "off";
    return <components.Input {...props} autoComplete={autoComplete} />;
};

const selectComponents = {
    DropdownIndicator: CustomDropdownIndicator,
    Input: InputWithAutoComplete,
};

interface ReactAsyncSelectProps {
    name?: string;
    value: Option | null;
    options: Array<any>;
    placeholder: string;
    onChange: (option: Option | null) => void;
    isSearchable: boolean;
    disabled?: boolean;
    key?: string;
    isLoading?: boolean;
    loadOptions?: any;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    id?: string;
    inputAutoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
    onMenuOpen?: () => void;
    onMenuClose?: () => void;
    onMenuScrollToBottom?: () => void;
    onInputChange?: SelectProps<Option, false>["onInputChange"];
    noOptionsMessage?: SelectProps<Option, false>["noOptionsMessage"];
    loadingMessage?: SelectProps<Option, false>["loadingMessage"];
    disableLocalFilter?: boolean;
}

const filterOption = (option: Option, inputValue: string) => {
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
};

const ReactAsyncSelect: React.FC<ReactAsyncSelectProps> = ({
    name,
    value,
    loadOptions,
    placeholder,
    onChange,
    isSearchable,
    disabled = false,
    key,
    isLoading,
    id,
    onKeyDown,
    options,
    inputAutoComplete = "off",
    onMenuOpen,
    onMenuClose,
    onMenuScrollToBottom,
    onInputChange,
    noOptionsMessage,
    loadingMessage,
    disableLocalFilter = false,
}) => {
    const formikContext = useFormikContext<any>();
    const errors = formikContext?.errors && formikContext.errors[name];
    const touched = formikContext?.touched && formikContext.touched[name];
    const shouldUseControlledOptions = disableLocalFilter;
    const commonSelectProps = {
        id,
        name,
        key,
        inputId: id ?? name,
        value,
        placeholder,
        onChange,
        isSearchable,
        inputAutoComplete,
        styles: customStyles(errors, touched, false, false),
        isDisabled: disabled,
        onMenuOpen,
        onMenuClose,
        onMenuScrollToBottom,
        onInputChange,
        noOptionsMessage,
        loadingMessage,
        components: selectComponents,
        isLoading,
        onKeyDown,
        "data-error-field": name,
    };

    return (
        <div>
            {shouldUseControlledOptions ? (
                <Select {...commonSelectProps} options={options} filterOption={null} />
            ) : (
                <AsyncSelect
                    {...commonSelectProps}
                    defaultOptions={options}
                    loadOptions={loadOptions}
                    filterOption={filterOption}
                />
            )}
        </div>
    );
};

export default ReactAsyncSelect;
